import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CalendarOff, Plus, X, Save, Check, XCircle, Clock, Trash2, Filter,
} from "lucide-react";
import EmployeePicker from "@/components/EmployeePicker";
import ConfirmDialog from "@/components/ConfirmDialog";
import ModuleHint from "@/components/ModuleHint";
import FirmFilter from "@/components/FirmFilter";
import { EmptyState } from "@/components/Shared";

const inputCls = "w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:outline-none focus:border-blue-500";
const Lbl = ({ children, required }) => (
  <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
    {children}{required && <span className="text-red-400 ml-1">*</span>}
  </label>
);

const STATUS_BADGE = {
  pending:  { label: "Ausstehend", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30",   Icon: Clock },
  approved: { label: "Genehmigt",  cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", Icon: Check },
  rejected: { label: "Abgelehnt",  cls: "bg-rose-500/15 text-rose-300 border-rose-500/30",       Icon: XCircle },
};

export default function Abwesenheiten() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [filter, setFilter] = useState("");
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  async function load() {
    try {
      const [list, s] = await Promise.all([
        api.get("/absence-requests", { params: filter ? { status: filter } : {} }),
        api.get("/absence-requests/_summary"),
      ]);
      setItems(list.data || []);
      setSummary(s.data || {});
    } catch { toast.error("Konnte Anträge nicht laden"); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  async function approve(it) {
    try { await api.put(`/absence-requests/${it.id}/approve`); toast.success("Genehmigt"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }
  async function reject(it) {
    const reason = window.prompt("Ablehnungsgrund (optional):") ?? null;
    if (reason === null) return;
    try { await api.put(`/absence-requests/${it.id}/reject`, { reason }); toast.success("Abgelehnt"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }
  async function remove(it) {
    setConfirmDelete(it);
  }
  async function confirmDeleteRun() {
    if (!confirmDelete) return;
    setConfirmBusy(true);
    try { await api.delete(`/absence-requests/${confirmDelete.id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setConfirmBusy(false); setConfirmDelete(null); }
  }

  return (
    <div className="space-y-5" data-testid="abwesenheiten-page">
      <ModuleHint moduleKey="abwesenheiten" />
      <div>
        <h1 className="text-3xl font-display font-bold text-zinc-50">Abwesenheiten</h1>
        <p className="text-xs text-zinc-500 mt-1">Urlaubs-Anträge, Krankmeldungen, sonstige Abwesenheiten — Beantragen & Genehmigen.</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Gesamt"    value={summary.total}    color="zinc"   onClick={() => setFilter("")} active={!filter} />
        <SummaryCard label="Ausstehend" value={summary.pending}  color="amber"  onClick={() => setFilter("pending")}  active={filter === "pending"} />
        <SummaryCard label="Genehmigt"  value={summary.approved} color="emerald" onClick={() => setFilter("approved")} active={filter === "approved"} />
        <SummaryCard label="Abgelehnt"  value={summary.rejected} color="rose"   onClick={() => setFilter("rejected")} active={filter === "rejected"} />
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h4 className="font-display font-semibold text-zinc-50 text-sm flex items-center gap-2">
              <CalendarOff className="w-4 h-4 text-rose-400" /> Anträge
              {filter && <span className="text-[10px] uppercase tracking-wider text-zinc-500">· Filter: {filter}</span>}
            </h4>
            <p className="text-[11px] text-zinc-500 mt-0.5">{items.length} Einträge</p>
          </div>
          <div className="flex items-center gap-2">
            <FirmFilter compact />
            {filter && (
              <button onClick={() => setFilter("")} data-testid="abw-clear-filter"
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded">
                <Filter className="w-3 h-3" /> Filter löschen
              </button>
            )}
            <button onClick={() => setCreating(true)} data-testid="abw-new-request"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">
              <Plus className="w-3.5 h-3.5" /> Neuer Antrag
            </button>
          </div>
        </div>

        {items.length === 0 ? (
          <EmptyState label="Keine Anträge." />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <th className="px-5 py-3 text-left font-semibold">Mitarbeiter</th>
                <th className="px-3 py-3 text-left font-semibold">Abwesenheit</th>
                <th className="px-3 py-3 text-left font-semibold w-32">Von</th>
                <th className="px-3 py-3 text-left font-semibold w-32">Bis</th>
                <th className="px-3 py-3 text-left font-semibold w-32">Status</th>
                <th className="px-3 py-3 text-right font-semibold w-44">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const sb = STATUS_BADGE[it.status] || STATUS_BADGE.pending;
                const SIcon = sb.Icon;
                return (
                  <tr key={it.id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40"
                    data-testid={`abw-row-${it.id}`}>
                    <td className="px-5 py-2.5 text-zinc-100">{it.employee_name || it.employee_id?.slice(0, 8)}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded" style={{ background: it.absence_color || "#3B82F6" }} />
                        <span className="text-zinc-100">{it.absence_name || "—"}</span>
                        {it.absence_abbreviation && <span className="text-[9px] uppercase font-mono text-zinc-500">({it.absence_abbreviation})</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-zinc-300 font-mono text-xs">{it.date_from}</td>
                    <td className="px-3 py-2.5 text-zinc-300 font-mono text-xs">{it.date_to}</td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${sb.cls}`}>
                        <SIcon className="w-3 h-3" /> {sb.label}
                      </span>
                      {it.status === "rejected" && it.rejection_reason && (
                        <div className="text-[10px] text-zinc-500 mt-0.5 italic">{it.rejection_reason}</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="inline-flex gap-1">
                        {it.status === "pending" && (
                          <>
                            <button onClick={() => approve(it)} data-testid={`abw-approve-${it.id}`}
                              title="Genehmigen"
                              className="p-1.5 text-emerald-400 hover:bg-emerald-500/10 rounded">
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => reject(it)} data-testid={`abw-reject-${it.id}`}
                              title="Ablehnen"
                              className="p-1.5 text-rose-400 hover:bg-rose-500/10 rounded">
                              <XCircle className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                        <button onClick={() => remove(it)} data-testid={`abw-delete-${it.id}`}
                          title="Löschen"
                          className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {creating && <RequestModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      <ConfirmDialog
        open={!!confirmDelete}
        title="Antrag löschen?"
        message="Diese Aktion kann nicht rückgängig gemacht werden. Genehmigte Abwesenheiten werden ebenfalls aus der Lohnabrechnung entfernt."
        confirmLabel="Ja, löschen"
        tone="danger"
        busy={confirmBusy}
        onConfirm={confirmDeleteRun}
        onCancel={() => !confirmBusy && setConfirmDelete(null)}
        testid="abw-confirm-delete"
      />
    </div>
  );
}

function SummaryCard({ label, value, color, onClick, active }) {
  const colors = {
    zinc:    "border-zinc-700",
    amber:   "border-amber-500/40",
    emerald: "border-emerald-500/40",
    rose:    "border-rose-500/40",
  };
  return (
    <button onClick={onClick} type="button"
      data-testid={`abw-summary-${label.toLowerCase()}`}
      className={`text-left p-4 bg-zinc-900 border rounded-md transition-colors ${active ? "ring-2 ring-blue-500/50" : ""} ${colors[color]} hover:bg-zinc-800/40`}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
      <div className="text-2xl font-display font-bold text-zinc-50 mt-1">{value ?? 0}</div>
    </button>
  );
}

function RequestModal({ onClose, onSaved }) {
  const [employees, setEmployees] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [form, setForm] = useState({ employee_id: "", absence_id: "", date_from: today(), date_to: today(), notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/employees"),
      api.get("/arbeitszeit/absences"),
    ]).then(([e, a]) => { setEmployees(e.data || []); setAbsences(a.data || []); })
      .catch(() => toast.error("Lade-Fehler"));
  }, []);

  function up(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  const selectedAbsence = useMemo(() => absences.find((a) => a.id === form.absence_id), [absences, form.absence_id]);

  async function submit() {
    if (!form.employee_id) { toast.error("Mitarbeiter wählen"); return; }
    if (!form.absence_id) { toast.error("Abwesenheits-Typ wählen"); return; }
    if (!form.date_from || !form.date_to) { toast.error("Datum erforderlich"); return; }
    if (form.date_from > form.date_to) { toast.error("'Bis' muss ≥ 'Von' sein"); return; }
    setSaving(true);
    try {
      await api.post("/absence-requests", form);
      toast.success(selectedAbsence?.auto_accept ? "Antrag automatisch genehmigt" : "Antrag erstellt");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-xl my-8 flex flex-col overflow-hidden max-h-[88vh]"
        onClick={(e) => e.stopPropagation()} data-testid="abw-modal">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between flex-shrink-0">
          <h3 className="font-display font-semibold text-zinc-100">Neuer Abwesenheits-Antrag</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <div>
            <Lbl required>Mitarbeiter</Lbl>
            <EmployeePicker value={form.employee_id} testid="abw-modal-emp-picker"
              includeInactive={false}
              placeholder="— Aktiver Mitarbeiter wählen —"
              onChange={(v) => up("employee_id", v)} />
          </div>
          <div>
            <Lbl required>Abwesenheits-Typ</Lbl>
            <select value={form.absence_id} onChange={(e) => up("absence_id", e.target.value)}
              data-testid="abw-modal-abs" className={inputCls}>
              <option value="">— wählen —</option>
              {absences.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} {a.abbreviation ? `(${a.abbreviation})` : ""}
                </option>
              ))}
            </select>
            {selectedAbsence?.auto_accept && (
              <p className="text-[10px] text-emerald-400 mt-1 inline-flex items-center gap-1">
                <Check className="w-3 h-3" /> Wird automatisch genehmigt
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Lbl required>Von</Lbl>
              <input type="date" value={form.date_from} onChange={(e) => up("date_from", e.target.value)}
                data-testid="abw-modal-from" className={inputCls} />
            </div>
            <div>
              <Lbl required>Bis</Lbl>
              <input type="date" value={form.date_to} onChange={(e) => up("date_to", e.target.value)}
                data-testid="abw-modal-to" className={inputCls} />
            </div>
          </div>
          <div>
            <Lbl>Notizen</Lbl>
            <textarea rows={3} value={form.notes} onChange={(e) => up("notes", e.target.value)} className={inputCls} />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2 flex-shrink-0">
          <button onClick={onClose} className="px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded">Abbrechen</button>
          <button onClick={submit} disabled={saving} data-testid="abw-modal-save"
            className="inline-flex items-center gap-1 px-4 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded">
            <Save className="w-3.5 h-3.5" /> {saving ? "Sende…" : "Antrag stellen"}
          </button>
        </div>
      </div>
    </div>
  );
}

function today() { return new Date().toISOString().slice(0, 10); }
